'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { useRouter } from 'next/navigation';

import dynamic from 'next/dynamic';

import { useOffers } from '@/api/useOffers';
import { useStoreBoot } from '@/api/useStoreBoot';

import { useUrlQuery } from '@/hooks/useUrlQuery';
import { EOfferType, EPopupSubType, EQueryParams, ESessionStorageKeys, ESource } from '@/lib/enums';
import { BootData, OfferData, OfferDataForPublisherEvent, OrderData } from '@/lib/types';
import { extractOfferMainDetails, getSelectedProduct } from '@/lib/utils';
import { getShouldDisplayCheckout } from './checkout.utils';
import './checkoutPopstateDismiss';
import { useAppState } from '@/state/hooks/app.state.hook';
import { useLoginState } from '@/state/hooks/login.state.hook';
import { usePopupState } from '@/state/hooks/popup.state.hook';
import { webStorageUtil } from '@/state/webStorage.state.service';
import { retryChunkImport } from '@/lib/retryChunkImport';

const CAMPAIGN_SOURCE = 'campaign';

// Everything below renders conditionally (a purchase, a failure, a collect) or
// draws nothing until one happens, so none of it belongs in the chunk every
// store-capable page downloads before first paint. Each loads on demand and
// retries dropped requests like the rest of the portal's split points.
//
// ProductsCollectAnimation is imported from its own esm module: a dynamic
// import of the package barrel would link the whole library into one async
// chunk, recreating the monolith this app just stopped shipping.
const ProductsCollectAnimation = dynamic(
  () =>
    retryChunkImport(() =>
      import('@appcharge/shared-ui/esm/components/ProductsCollectAnimation/index.js').then(
        (mod) => mod.ProductsCollectAnimation
      )
    ),
  { ssr: false }
);

const StoreConfetti = dynamic(
  () => retryChunkImport(() => import('./StoreConfetti').then((mod) => mod.StoreConfetti)),
  { ssr: false }
);

const Checkout = dynamic(
  () =>
    retryChunkImport(() =>
      import('../components/Checkout/Checkout').then((mod) => mod.Checkout)
    ),
  { ssr: false }
);

const PopupFailScreen = dynamic(
  () =>
    retryChunkImport(() =>
      import('../components/PopupFailScreen/PopupFailScreen').then((mod) => mod.PopupFailScreen)
    ),
  { ssr: false }
);

export const StoreContent: React.FC<{
  offerIds?: string[];
}> = ({ offerIds }) => {
  const router = useRouter();
  const { isLoggedIn } = useLoginState();

  const from = useUrlQuery(EQueryParams.FROM);
  const campaignToken = useUrlQuery(EQueryParams.TOKEN);
  const [campaignOfferId, setCampaignOfferId] = useState<{ offerId: string }>();
  const [loadingFromCampaign, setLoadingFromCampaign] = useState(from === CAMPAIGN_SOURCE);

  const [stubOffer, setStubOffer] = useState(false);

  const { getOffers, getStoreCampaign } = useOffers({ offerIds });
  const { getBoot } = useStoreBoot();

  const bootData = getBoot.data as BootData;

  const appState = useAppState();

  const {
    setDisplayLoader,
    selectedOffer,
    activeOrderId,
    setActiveOrderId,
    setSelectedOffer,
    collectedProductsImages,
    setCollectedProductsImages,
    showConfetti,
    setShowConfetti,
    showPaymentModal,
    setShowPaymentModal,
    setCampaignOfferData,
    campaignOfferData,
  } = appState;

  const { setIsLoginModalOpen } = useLoginState();

  const popupState = usePopupState();
  const { currentPopupDisplayed, setCurrentPopupDisplayed, popupIndexToCollect } = popupState;

  // Trigger getOffers query when user is logged in and store page loads
  useEffect(() => {
    if (isLoggedIn && !getOffers.data && !getOffers.isLoading) {
      getOffers.refetch();
    }
  }, [isLoggedIn, getOffers.data, getOffers.isLoading]);

  // Trigger getBoot query when user is logged in and store page loads
  useEffect(() => {
    if (isLoggedIn && !getBoot.data && !getBoot.isLoading) {
      getBoot.refetch();
    }
  }, [isLoggedIn, getBoot.data, getBoot.isLoading]);

  const shouldDisplayProductsAnimation = (order: OrderData): boolean => {
    const price = order.price;
    const isRollingOffer = order.offerType === EOfferType.ROLLING_OFFER;

    const productsAnimationCollect =
      bootData?.featureFlags?.store_collect_animation_post_free_collect_promotions_popups === true;
    const productsAnimationPurchase =
      bootData?.featureFlags?.store_animation_after_purchases === true;

    if (isRollingOffer) {
      return true;
    }

    if (price === 0) {
      return productsAnimationCollect;
    }

    return productsAnimationPurchase;
  };

  const currencyCode = useMemo(() => {
    if (!selectedOffer || typeof selectedOffer === 'string') return;

    const selectedProduct = getSelectedProduct(
      selectedOffer as OfferData,
      popupIndexToCollect || 0
    );

    return selectedProduct?.prices?.currencyCode;
  }, [selectedOffer]);

  const closePopup = () => {
    setDisplayLoader(false);
    setShowPaymentModal(false);
    setActiveOrderId('');
    setCurrentPopupDisplayed(null);
    setSelectedOffer(null);
    setCampaignOfferId(undefined);
    setCampaignOfferData(undefined);
    document.getElementById('root')?.classList.remove('screen-lock');
  };

  useEffect(() => {
    if (from === CAMPAIGN_SOURCE && campaignToken) {
      getStoreCampaign.mutate(
        {
          tokenId: campaignToken,
        },
        {
          onSuccess: (data: any) => {
            setCampaignOfferId(data);
          },
        }
      );
    }
  }, [from, campaignToken]);

  useEffect(() => {
    if (
      from !== CAMPAIGN_SOURCE ||
      !campaignOfferId ||
      getOffers?.isLoading ||
      !getOffers.data?.offers
    )
      return;

    const currentCampaignOffer = getOffers?.data?.offers?.promotions?.find(
      (promotion: OfferData) => promotion.offerId === campaignOfferId.offerId
    );

    if (currentCampaignOffer) {
      setCampaignOfferData(extractOfferMainDetails([currentCampaignOffer])[0]);
    } else if (from !== CAMPAIGN_SOURCE) {
      setStubOffer(true);
    } else if (typeof campaignOfferId.offerId === 'string') {
      setSelectedOffer(campaignOfferId.offerId);
      setShowPaymentModal(true);
    } else {
      console.log('campaign offer does not exist');
      router.replace('/failed?message=offer not found');
    }

    return () => {
      setCampaignOfferData(undefined);
    };
  }, [campaignOfferId, getOffers?.data, from]);

  const shouldDisplayCheckout: boolean = useMemo(
    () =>
      getShouldDisplayCheckout({
        selectedOffer,
        showPaymentModal,
        campaignOfferData,
        activeOrderId,
        catalogOffers: getOffers.data?.offers,
      }),
    [selectedOffer, showPaymentModal, campaignOfferData, activeOrderId, getOffers.data?.offers]
  );

  if (!isLoggedIn) {
    setIsLoginModalOpen(true);
    webStorageUtil.set(ESessionStorageKeys.SOURCE, ESource.STORE_TAB);

    return;
  }

  if (getOffers.isLoading || getOffers.isFetching || !getBoot.data || !getOffers.data) {
    return null;
  }

  return (
    <div className="store-content flex flex-1 overflow-hidden text-white">
      <ProductsCollectAnimation
        refetchOffers={getOffers.refetch}
        collectedProductsImages={collectedProductsImages ?? []}
        setCollectedProductsImages={setCollectedProductsImages}
        shouldDisplayProductsAnimation={shouldDisplayProductsAnimation}
        setIsLoading={setDisplayLoader}
        isDailyBonus={currentPopupDisplayed?.subType === EPopupSubType.DAILY_BONUS}
      />

      {showConfetti && (
        <StoreConfetti
          onComplete={() => {
            setShowConfetti(false);
          }}
        />
      )}

      <PopupFailScreen />

      {shouldDisplayCheckout && (
        <Checkout
          selectedOffer={
            (campaignOfferData as OfferDataForPublisherEvent) || (selectedOffer as OfferData)
          }
          stubOffer={stubOffer}
          setStubOffer={setStubOffer}
          close={() => closePopup()}
          setLoadingFromCampaign={setLoadingFromCampaign}
          collectId={activeOrderId}
          currencyCode={currencyCode}
        />
      )}
    </div>
  );
};
